<template>
    <div class="patient-surveys">

        <!-- invitable questionnaires -->

        <table class="table table-striped"
               :class="{'table-responsive': $mq == 'sm'}">
            <thead>
            <tr>
                <th><label>{{ $i18n.gt('LABEL_QUESTIONNAIRE') }}</label></th>
                <th><label>{{ $i18n.gt('LABEL_CREATED') }}</label></th>
                <th><label>{{ $i18n.gt('LABEL_STARTED') }}</label></th>
                <th><label>{{ $i18n.gt('LABEL_UPDATED') }}</label></th>
                <th class="text-left"><label>{{ $i18n.gt('LABEL_STATUS') }}</label></th>
                <th class="text-center"><label>{{ $i18n.gt('LABEL_CREATE') }}</label></th>
                <th></th>
            </tr>
            </thead>
            <tr v-for="(surveyRow,i) in invitableSurveys" :key="i">
                <th>
                    {{ $i18n.gt(`QUESTIONNAIRE_TITLE_${surveyRow.questionnaire_code}`) }}
                </th>
                <td>
                    <timestamp :date="surveyRow.created"></timestamp>
                </td>
                <td>
                    <timestamp :date="surveyRow.started"></timestamp>
                </td>
                <td>
                    <timestamp :date="surveyRow.lastupdated"></timestamp>
                </td>
                <td class="text-left">
                    <b-badge variant="success"
                             v-if="surveyRow.is_complete">
                        {{ $i18n.gt('BADGE_COMPLETED') }}
                    </b-badge>
                    <b-badge variant="danger"
                             v-else-if="!!surveyRow.survey_id && !surveyRow.started">
                        {{ $i18n.gt('BADGE_UNSTARTED') }}
                    </b-badge>
                    <b-badge variant="warning"
                             v-else-if="!!surveyRow.survey_id">
                        {{ $i18n.gt('BADGE_INCOMPLETE') }}
                    </b-badge>
                    <!-- view responses -->
                    <router-link :to="`/survey/responses/participantId/${participant.id}/id/${surveyRow.survey_id}`"
                                 v-if="!!surveyRow.survey_id && surveyRow.questionnaire_code != 'DIETID'">
                        <b-icon-search></b-icon-search>
                    </router-link>
                </td>
                <td class="text-center">
                    <b-form-checkbox v-model="surveyRow.invite"
                                     @change="inviteChecked(surveyRow, $event)">
                    </b-form-checkbox>
                </td>
                <!-- inspect/create/delete (admin only) -->
                <td class="text-right" v-if="$user.isMaskingAdmin() || $user.roleIsAdmin()">
                    <b-dropdown right variant="primary" size="sm">
                        <template #button-content>
                            <shs-icon name="cog"></shs-icon>
                        </template>
                        <b-dropdown-item>
                            <label>{{ $i18n.gt('LABEL_SURVEY_ID') }}: {{ surveyRow.survey_id }}</label>
                        </b-dropdown-item>
                        <b-dropdown-item
                            v-if="!!surveyRow.survey_id && surveyRow.questionnaire_code != 'DIETID'"
                            :to="`/survey/show/participantId/${participant.id}/id/${surveyRow.survey_id}`">
                            {{ $i18n.gt('LABEL_INSPECT') }}
                        </b-dropdown-item>
                        <b-dropdown-item @click="createSurvey(surveyRow.questionnaire_code)">
                            {{ $i18n.gt('LABEL_CREATE_NEW') }}
                        </b-dropdown-item>
                        <b-dropdown-item v-if="surveyRow.survey_id"
                                         @click="deleteSurvey(surveyRow.survey_id)">
                            {{ $i18n.gt('LABEL_DELETE') }}
                        </b-dropdown-item>
                    </b-dropdown>
                </td>
            </tr>
        </table>

        <div class="float-right mr-table-2">
            <b-btn variant="primary"
                   :disabled="!questionnaireCodes.length || creatingSurveys"
                   @click="createSurveys()">
                <span v-show="!creatingSurveys">
                    {{ $i18n.gt('LABEL_CREATE_SURVEYS') }}
                </span>
                <span v-show="creatingSurveys">
                {{ $i18n.gt('LABEL_CREATING_SURVEYS') }}
                <shs-icon name="timer"></shs-icon>
            </span>
            </b-btn>
        </div>
        <div class="clearfix"></div>

        <hr/>

        <!-- non-invitable questionnaires -->

        <h3>{{ $i18n.gt('HEADING_PROVIDER_ADMINISTERED_QUESTIONNAIRES') }}</h3>
        <table class="table table-striped" :class="{'table-responsive': $mq == 'sm'}">
            <thead>
            <tr>
                <th><label>{{ $i18n.gt('LABEL_QUESTIONNAIRE') }}</label></th>
                <th class="text-center"><label>{{ $i18n.gt('LABEL_STARTED') }}</label></th>
                <th class="text-center"><label>{{ $i18n.gt('LABEL_STATUS') }}</label></th>
                <th class="text-center"><label></label></th>
                <th></th>
            </tr>
            </thead>
            <tr v-for="(surveyRow, i) in nonInvitableSurveys" :key="i">
                <th>
                    {{ $i18n.gt(`QUESTIONNAIRE_TITLE_${surveyRow.questionnaire_code}`) }}
                </th>
                <td class="text-center">
                    <timestamp :date="surveyRow.started"></timestamp>
                </td>
                <td class="text-center">
                    <b-badge variant="success"
                             v-if="surveyRow.is_complete">
                        {{ $i18n.gt('BADGE_COMPLETED') }}
                    </b-badge>
                    <b-badge variant="danger"
                             v-else-if="!surveyRow.started">
                        {{ $i18n.gt('BADGE_UNSTARTED') }}
                    </b-badge>
                    <b-badge variant="warning"
                             v-else>
                        {{ $i18n.gt('BADGE_INCOMPLETE') }}
                    </b-badge>
                </td>
                <td class="text-center">
                    <div v-if="surveyRow.started && !surveyRow.is_complete">
                        <b-btn size="sm" class="btn-icon-text"
                               :to="`/survey/doctorFill/id/${surveyRow.survey_id}/participantId/${participant.id}`">
                            {{ $i18n.gt('LABEL_CONTINUE') }}
                            <shs-icon name="pencil"></shs-icon>
                        </b-btn>
                    </div>
                    <div v-else>
                        <b-btn size="sm" class="btn-icon-text"
                               :to="`/survey/doctorCreate/questionnaireCode/${surveyRow.questionnaire_code}/participantId/${participant.id}`">
                            {{ $i18n.gt('LABEL_CREATE') }}
                            <shs-icon name="pencil"></shs-icon>
                        </b-btn>
                    </div>
                </td>
                <td class="text-right" v-if="$user.isMaskingAdmin() || $user.roleIsAdmin()">
                    <b-dropdown right variant="primary" size="sm">
                        <template #button-content>
                            <shs-icon name="cog"></shs-icon>
                        </template>
                        <b-dropdown-item>
                            <label>{{ $i18n.gt('LABEL_SURVEY_ID') }}: {{ surveyRow.survey_id }}</label>
                        </b-dropdown-item>
                        <b-dropdown-item
                            v-if="surveyRow.questionnaire_code != 'DIETID'"
                            :to="`/survey/show/participantId/${participant.id}/id/${surveyRow.survey_id}`">
                            {{ $i18n.gt('LABEL_INSPECT') }}
                        </b-dropdown-item>
                        <b-dropdown-item @click="deleteSurvey(surveyRow.survey_id)">
                            {{ $i18n.gt('LABEL_DELETE') }}
                        </b-dropdown-item>
                    </b-dropdown>
                </td>

            </tr>
        </table>

    </div>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import * as _ from 'lodash'
import PractitionerPatientMixin from "@/providers/PractitionerPatientMixin"
import Vue from "vue";

/**
 * Listing of patient's surveys with status, invitation functions
 */
export default Vue.extend({
    name: 'PatientSurveys',
    mixins: [PractitionerPatientMixin],
    props: {
        participantId: Number
    },
    data() {
        return {
            creatingSurveys: false,
        }
    },
    computed: {
        useDietId() {
            return this.$store.state.patientParticipantProviderUsesDietId
        },
        invitableSurveys() {
            return _.filter(this.participant.latestSurveys, (surveyRow) => {
                const WC_ONLY = ['WBI', 'RATIO'] // Questionnaires to be shown only in Wellcoaches theme
                if (!this.$utils.themeIs('wellcoaches') && WC_ONLY.includes(surveyRow.questionnaire_code)) {
                    return false
                }
                if (!this.useDietId && surveyRow.questionnaire_code == 'DIETID') {
                    return false
                } else if (this.useDietId && surveyRow.questionnaire_code == 'DIET') {
                    return false
                } else {
                    return surveyRow.is_invitable
                }
            })
        },
        nonInvitableSurveys() {
            return _.filter(this.participant.latestSurveys, (surveyRow) => {
                return !surveyRow.is_invitable
            })
        },
        questionnaireCodes() {
            const codes = []
            _.each(this.participant.latestSurveys, (row) => {
                if (row.invite) {
                    codes.push(row.questionnaire_code)
                }
            })
            return codes
        }
    },
    methods: {
        async createSurvey(questionnaireCode) {
            try {
                await this.$api.createSurvey(this, questionnaireCode, this.participantId)
                this.loadParticipant(true)
            } catch (e) {
                this.$utils.handleError(e, 'Error creating survey')
            }
        },
        async deleteSurvey(surveyId) {
            if (await this.$bvModal.msgBoxConfirm('Delete this survey permanently?')) {
                try {
                    await this.$api.deleteItem(this, 'survey', {id: surveyId})
                    this.$toasted.show('Questionnaire Deleted', {duration: 3000})
                    this.loadParticipant(true) // reload participant and surveys
                } catch (e) {
                    this.$utils.handleError(e, 'Error deleting survey')
                }
            }
        },
        inviteChecked(surveyRow, checked) {
            const awvCodeChecked = this.isAwvSurveyRow(surveyRow) && checked
            const noOthersChecked = !_.find(this.invitableSurveys, (row) => {
                return row != surveyRow && this.isAwvSurveyRow(row) && row.invite
            })
            if (awvCodeChecked && noOthersChecked) {
                _.each(this.invitableSurveys, (row) => {
                    if (this.isAwvSurveyRow(row)) {
                        Vue.set(row, 'invite', true) // NB: Vue.set is necessary here (?)
                    }
                })
            }
        },
        isAwvSurveyRow(surveyRow) {
            const awvCodes = ['MH', 'DIET', 'DIETID', 'STRESS', 'EXERCISE', 'SLEEP']
            return awvCodes.indexOf(surveyRow.questionnaire_code) != -1
        },
        clearQuestionnaireCodes() {
            _.each(this.participant.latestSurveys, (row) => {
                row.invite = false
            })
        },
        async createSurveys() {
            try {
                this.creatingSurveys = true
                await this.$api.createSurveys(this,
                    this.participant.id,
                    this.provider.practice_id,
                    this.questionnaireCodes)
                this.clearQuestionnaireCodes()
                this.$toasted.show('Questionnaires created', {duration: 3000})
            } catch (e) {
                this.$utils.handleError(e, 'Error creating questionnaires')
                this.$api.log(this, 'participant', null, 'createSurveys',
                    `Error: Could not create surveys for Participant #${this.participant.id}, for Questionnaires ${this.questionnaireCodes.join(', ')}`)
            }
            this.creatingSurveys = false
            this.loadParticipant(true)
        },
    },
})
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">
.patient-surveys {
    h5 {
        text-transform: none !important;
    }
}
</style>
